/**
 * Student Service — Hybrid (Supabase Direct + TS API)
 * Also exports backward-compatible aliases used by legacy components.
 *
 * Replaces: components/services/sharedAPI/Student.ts
 *           components/services/sharedAPI/useGetStudentsApi.ts
 *           components/services/sharedAPI/useGetStudentApi.ts
 * Old path:  LMSGradebookInstance → Rails gradebook:3002 → DynamoDB
 * New path:  Supabase client for student data; TS API for student assessments
 *
 * Basic student queries go direct to Supabase.
 * Student assessments (computed join with grades/attempts) go through TS API.
 */
import { useCallback, useState } from 'react';
import { useSupabaseList, useSupabaseGet, type CrudCallbacks } from '../useSupabaseCrud';
import { useQueryList } from '../query/useQueryList';
import type { Tables } from '../supabase';
import type { GradeLevelAssignment } from '@components/models/student/StudentModel';
import { getErrorMessage } from "@utils/helper";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Student = Tables<'students'>;

/**
 * Embed-aware row shape returned by useGetStudent / useGetStudents.
 *
 * `students` has NO grade_level_id / school_year_id / class_id columns of its
 * own — the select (below) embeds them additively via the
 * `student_grade_levels` junction (aliased `grade_level`) and `class_students`.
 * This type ONLY narrows what the hooks already fetch; the runtime query is
 * unchanged. Mirrors the `CourseWithRelations` pattern in services/shared/course.ts.
 *
 * `grade_level` is typed as the shared `GradeLevelAssignment` model so this row
 * stays structurally assignable to `StudentModel` at the context boundaries
 * (GradeBookDetailsContext assigns `response` → `currentStudent: StudentModel`).
 * That model carries `schoolYear` (the embed alias) + `grade_level.grade_level`.
 *
 * NOTE: periodState / returnReason are read on `grade_level` by the registrar /
 * parent-teacher GradeBookDetails banner, but the select below does NOT fetch
 * them — they are a genuine missing-data gap, not a typing gap, so they are
 * intentionally absent here.
 */
export interface StudentWithGradeLevel extends Tables<'students'> {
  users: Pick<
    Tables<'users'>,
    'email' | 'first_name' | 'last_name' | 'base_role' | 'status'
  > | null;
  grade_level: GradeLevelAssignment | null;
  class_students: Pick<Tables<'class_students'>, 'class_id'>[] | null;
}

const getApi = () => import('../api').then(m => m.api);

// ---------------------------------------------------------------------------
// useGetStudents — List students (direct Supabase)
// ---------------------------------------------------------------------------

export interface GetStudentsParams {
  page?: number;
  /**
   * Rows per fetch. Default 25 (one server page). RegistrarStudents passes a
   * large value to pull the WHOLE roster in a single read so the table can
   * search / sort / paginate the FULL set client-side — the stat columns (GPA /
   * semester avg / progress / last access) are computed client-side and are NOT
   * DB columns, so they can't be searched or sorted server-side. Only used by
   * the React Query hook (useGetStudentsQuery / useGetStudentsApiQuery).
   */
  pageSize?: number;
}

export const useGetStudents = (defaultParams?: GetStudentsParams) => {
  const list = useSupabaseList<'students', GetStudentsParams, StudentWithGradeLevel>(defaultParams ?? {}, {
    table: 'students',
    // `students` has NO grade_level_id / class_id / school_year_id columns of
    // its own — grade level + school year live on the student_grade_levels
    // junction, and class membership on class_students. Embed both additively
    // so consumers can read currentStudent.grade_level.{grade_level.grade_level,
    // schoolYear.name/periods, school_year_id} and class_students[].class_id.
    // Alias convention (grade_level:grade_levels / schoolYear:school_years)
    // mirrors services/admin/user.ts:78.
    select: '*, users!students_user_id_fkey(email, first_name, last_name, base_role, status), grade_level:student_grade_levels(grade_level_id, school_year_id, grade_level:grade_levels(grade_level), schoolYear:school_years(name, periods:grading_periods(id, period_name, start_date, end_date))), class_students(class_id)',
    orderBy: 'created_at',
    ascending: false,
    pageSize: 25,
  });

  const getStudents = (callbacks?: CrudCallbacks<Student[]>) => {
    list.fetchList(undefined, callbacks);
  };

  return {
    ...list,
    getStudents,
    // Backward-compatible aliases
    loading: list.processing,
    students: list.response,
  };
};

// ---------------------------------------------------------------------------
// useGetStudentsQuery — React Query version of useGetStudents
// ---------------------------------------------------------------------------
//
// React-Query twin built on `useQueryList`. Additive — the legacy hook (and its
// `useGetStudentsApi` wrapper) stay for the low-traffic / dashboard call-sites;
// the high-traffic student LIST screens (RegistrarStudents) use this one for
// caching + dedup + invalidation. The table / select / orderBy / pageSize are
// copied VERBATIM from useGetStudents above so the embed shape (the
// student_grade_levels + class_students joins) and the rows are byte-for-byte
// identical — only the cache/loading ownership moves to React Query.
//
// The `StudentWithGradeLevel` row generic keeps `response` typed against those
// embeds (same as useGetStudents). `students` / `loading` aliases mirror the
// legacy hook so a call-site can swap with minimal churn. See
// services/query/README.md.
export const useGetStudentsQuery = (params: GetStudentsParams = {}) => {
  // `pageSize` is a hook option, not a query filter — split it out so it never
  // reaches useQueryList's auto `.eq(key, value)` loop (it isn't a column).
  const { pageSize, ...filterParams } = params;
  const list = useQueryList<'students', GetStudentsParams, StudentWithGradeLevel>(filterParams, {
    table: 'students',
    select: '*, users!students_user_id_fkey(email, first_name, last_name, base_role, status), grade_level:student_grade_levels(grade_level_id, school_year_id, grade_level:grade_levels(grade_level), schoolYear:school_years(name, periods:grading_periods(id, period_name, start_date, end_date))), class_students(class_id)',
    queryKey: ['students', 'list'],
    orderBy: 'created_at',
    ascending: false,
    pageSize: pageSize ?? 25,
  });

  return {
    ...list,
    // Backward-compatible aliases (mirror useGetStudents)
    loading: list.processing,
    students: list.response,
  };
};

/**
 * React Query twin of `useGetStudentsApi`. Wraps `useGetStudentsQuery` and
 * re-shapes `students` into the `{ students: [...] }` envelope the legacy
 * AssignCourse / RegistrarStudents call-sites destructure. Additive — the
 * legacy `useGetStudentsApi` stays for the other (dashboard) call-sites.
 */
export const useGetStudentsApiQuery = (params?: GetStudentsParams) => {
  const hook = useGetStudentsQuery(params);
  return {
    ...hook,
    students: { students: hook.response ?? [] },
    loading: hook.loading,
  };
};

// ---------------------------------------------------------------------------
// useGetStudent — Single student by id (direct Supabase)
// ---------------------------------------------------------------------------

export const useGetStudent = (student_id: string) => {
  const single = useSupabaseGet<'students', StudentWithGradeLevel>(
    'students',
    // See useGetStudents above — grade level + school year live on the
    // student_grade_levels junction (with grade_levels / school_years joins),
    // class on class_students. GradeBookDetails(Context) + StudentProfile read
    // currentStudent.grade_level.{school_year_id, grade_level.grade_level,
    // schoolYear.name/periods}. Use the `schoolYear` alias (not `cycle`) to
    // match those component reads.
    '*, users!students_user_id_fkey(email, first_name, last_name, base_role, status), grade_level:student_grade_levels(active, grade_level_id, school_year_id, grade_level:grade_levels(grade_level), schoolYear:school_years(name, periods:grading_periods(id, period_name, start_date, end_date))), class_students(class_id)',
    student_id,
  );

  return {
    ...single,
    loading: single.processing,
    studentInfo: single.response,
    getStudentInfo: single.fetchOne,
  };
};

// ---------------------------------------------------------------------------
// useGetStudentAssessments — Student assessments with grades (TS API)
// ---------------------------------------------------------------------------

export const useGetStudentAssessments = (student_id: string) => {
  const [response, setResponse] = useState<any[] | null>(null);
  const [processing, setProcessing] = useState(false);
  const [data, setData] = useState<{ grade_level_id?: string }>({});

  const setFieldData = useCallback(
    (field: string, value: any) => setData(prev => ({ ...prev, [field]: value })),
    [],
  );

  const getStudentAssessments = useCallback(
    async (callbacks?: CrudCallbacks<any[]>) => {
      setProcessing(true);
      try {
        const api = await getApi();
        const params = new URLSearchParams();
        if (data.grade_level_id) params.set('grade_level_id', data.grade_level_id);
        const res = await api.get(`/api/grading/students/assessments/${student_id}?${params.toString()}`);
        setResponse(res.data);
        callbacks?.onSuccess?.(res.data);
      } catch (err: unknown) {
        callbacks?.onError?.({ general: getErrorMessage(err, 'Failed') });
      } finally {
        setProcessing(false);
      }
    },
    [student_id, data],
  );

  return {
    data,
    setData,
    setFieldData,
    processing,
    studentAssessments: response,
    getStudentAssessments,
  };
};

// ---------------------------------------------------------------------------
// Backward-compatible alias — used by teacher/admin AssignCourse components
// ---------------------------------------------------------------------------

export const useGetStudentsApi = (params?: GetStudentsParams) => {
  const hook = useGetStudents(params);
  return {
    ...hook,
    students: { students: hook.response ?? [] },
    // Backward compat: old components destructure these names
    getStudents: hook.getStudents,
    loading: hook.loading,
  };
};
